import { Icon } from "@iconify/react";
import { getNames } from "country-list";
import { Form, Formik, setIn } from "formik";
import {
  ReactNode,
  RefObject,
  useEffect,
  useMemo,
  useRef,
  useReducer,
  useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ReactSelect from "react-select";
import CreatableSelect from "react-select/creatable";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { CustomFieldsSection } from "../../components/shared/CustomFieldsSection";
import { Input, Label, TextArea } from "../../components/shared/InputField";
import { Select } from "../../components/shared/Select";
import { SectionStepper } from "../../components/shared/SectionStepper";
import type { SectionStep } from "../../components/shared/SectionStepper";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/shared/Card";
import { RadioGroup, RadioGroupItem } from "../../components/shared/RadioGroup";
import {
  isMinor,
  NomineesEditor,
} from "../../components/employees/NomineesEditor";
import {
  Box,
  ButtonPrimary,
  ButtonSecondary,
  HStack,
  IconCTAButton,
  IconSize,
  VStack,
} from "../../components/utils";
import { employeeTagOptions } from "../../modals/AddEmployee";
import {
  useAddEmployee,
  useCompanyManagers,
  useEmployeePageDetails,
  usePatchEmployee,
  useSubsidiaryCompanyNames,
} from "../../queries/employees";
import { useGetCustomFields } from "../../queries/customFields";
import {
  AddEmployeeReq,
  BoomerangCandidate,
  BoomerangResolutionStrategy,
  EmployementStatus,
  EmployementType,
} from "../../types/Employee";
import { UdfEntityType } from "../../types/CustomFields";
import { DematAccount } from "../../types/VestingTemplate";
import convertToBase64 from "../../utils/convertToBase64";
import { formatDate, formatWithTimeZone } from "../../utils/date";
import {
  buildEmployeePatch,
  normalizeEmployeeTags,
} from "../../utils/employeePatch";
import { encryptPii } from "../../utils/piiCrypto";
import { validateEmployeeStandardFields } from "../../utils/standardInputValidation";
import BCHeader from "../../shared/BCHeader";

const emptyValues: AddEmployeeReq = {
  employeeName: "",
  legalName: "",
  email: "",
  personalEmail: "",
  employeeIdentification: "",
  employmentType: EmployementType.EMPLOYEE,
  dateOfJoin: "",
  subsidiaryCompanyId: "",
  employeeDesignation: "",
  department: "",
  manager: "",
  managerIds: [],
  grade: "",
  salary: undefined,
  contactNumber: "",
  address: "",
  residentialStatus: "India",
  nationality: "India",
  notes: "",
  tags: [],
  dematAccounts: [],
  nominees: [],
  imageURL: "",
  pan: "",
  employmentStatus: EmployementStatus.EMPLOYED,
  company: "",
};

function currentLocalDate() {
  const now = new Date();
  const timezoneOffset = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - timezoneOffset).toISOString().slice(0, 10);
}

const requiredSchema = Yup.object({
  employeeName: Yup.string().trim().required("Name is required"),
  legalName: Yup.string().trim(),
  email: Yup.string().trim().email().required("Valid work email is required"),
  personalEmail: Yup.string()
    .trim()
    .email("Enter a valid personal email")
    .nullable(),
  employeeIdentification: Yup.string()
    .trim()
    .required("Employee ID is required"),
  dateOfJoin: Yup.string().required("Joining date is required"),
  employeeDesignation: Yup.string().trim().required("Designation is required"),
  department: Yup.string().trim().required("Department is required"),
  residentialStatus: Yup.string().required("Residence is required"),
  nationality: Yup.string().required("Nationality is required"),
  salary: Yup.number().nullable().min(0),
  contactNumber: Yup.string().test(
    "contact-number",
    "Enter a 10-digit contact number",
    (value) => !value || /^\d{10}$/.test(value)
  ),
  lastDay: Yup.string().when("employmentStatus", {
    is: EmployementStatus.RESIGNED,
    then: Yup.string().required("Last working day is required"),
  }),
  dematAccounts: Yup.array().of(
    Yup.object({
      dematAccountId: Yup.string()
        .matches(/^[A-Za-z0-9]{16}$/, "Enter a 16-character account ID")
        .required("Account ID is required"),
    })
  ),
  nominees: Yup.array()
    .of(
      Yup.object({
        name: Yup.string().trim().required("Name is required"),
        relationship: Yup.string().required("Relationship is required"),
        percentage: Yup.number()
          .moreThan(0, "Allocation must be greater than 0")
          .max(1, "Allocation cannot exceed 100%")
          .required("Allocation is required"),
        dateOfBirth: Yup.string()
          .required("Date of birth is required")
          .test(
            "not-future",
            "Date of birth cannot be in the future",
            (value) => !value || new Date(value) <= new Date()
          ),
        contact: Yup.string()
          .matches(/^\d{10}$/, "Enter a 10-digit contact number")
          .required("Contact number is required"),
        email: Yup.string().trim().email("Enter a valid email"),
        address: Yup.string().trim().required("Address is required"),
        guardian: Yup.object({
          name: Yup.string().trim(),
          relationship: Yup.string(),
          dateOfBirth: Yup.string().test(
            "not-future",
            "Date of birth cannot be in the future",
            (value) => !value || new Date(value) <= new Date()
          ),
          contact: Yup.string().test(
            "guardian-contact",
            "Enter a 10-digit contact number",
            (value) => !value || /^\d{10}$/.test(value)
          ),
          email: Yup.string().trim().email("Enter a valid email"),
          address: Yup.string().trim(),
        }).nullable(),
      })
    )
    .test(
      "allocation-total",
      "Nominee allocation must total 100%",
      (nominees) =>
        !nominees?.length ||
        Math.abs(
          nominees.reduce(
            (total, nominee) => total + Number(nominee.percentage || 0),
            0
          ) - 1
        ) < 0.0001
    )
    .test("minor-guardians", function validateGuardians(nominees) {
      if (!nominees) return true;
      for (let index = 0; index < nominees.length; index += 1) {
        const nominee = nominees[index];
        if (isMinor(nominee.dateOfBirth)) {
          const guardian = (nominee as any).guardian;
          const missingGuardianField = [
            ["name", guardian?.name],
            ["relationship", guardian?.relationship],
            ["dateOfBirth", guardian?.dateOfBirth],
            ["address", guardian?.address],
          ].find(([, value]) => !String(value || "").trim());
          if (missingGuardianField) {
            return this.createError({
              path: `nominees[${index}].guardian.${missingGuardianField[0]}`,
              message: "This guardian field is required",
            });
          }
          if (isMinor(guardian.dateOfBirth)) {
            return this.createError({
              path: `nominees[${index}].guardian.dateOfBirth`,
              message: "Guardian must be at least 18 years old",
            });
          }
          if (guardian.contact && !/^\d{10}$/.test(guardian.contact)) {
            return this.createError({
              path: `nominees[${index}].guardian.contact`,
              message: "Enter a 10-digit contact number",
            });
          }
          if (
            guardian.email &&
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guardian.email)
          ) {
            return this.createError({
              path: `nominees[${index}].guardian.email`,
              message: "Enter a valid email",
            });
          }
        }
      }
      return true;
    }),
});

function FieldBlock({
  label,
  children,
  required = false,
  hint,
  error,
}: {
  label: string;
  children: ReactNode;
  required?: boolean;
  hint?: string;
  error?: string | false;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-sm font-normal text-labelColor">
        {label}
        {required && <span className="ml-1 text-primary">*</span>}
      </Label>
      {children}
      {error ? (
        <p className="text-xs text-red-500">{error}</p>
      ) : hint ? (
        <p className="text-xs text-gray-light">{hint}</p>
      ) : null}
    </div>
  );
}

const MATCH_REASON_LABELS = {
  WORK_EMAIL: "Work email match",
  PERSONAL_EMAIL: "Personal email match",
  EMPLOYEE_ID: "Employee ID match",
  NAME: "Name match",
};

const RESOLUTION_OPTIONS: Array<{
  value: BoomerangResolutionStrategy;
  title: string;
  description: string;
}> = [
  {
    value: "NOT_BOOMERANG",
    title: "Not a boomerang",
    description:
      "Create an unrelated employee after changing any conflicting identifiers.",
  },
  {
    value: "MERGE_EXISTING_EMPLOYEE",
    title: "Link and merge employee records",
    description:
      "Update the former employee record and preserve the previous details in the activity log.",
  },
  {
    value: "CREATE_SEPARATE_EMPLOYEE",
    title: "Link and create a separate employee",
    description:
      "Keep both employment records linked and create a new portal login.",
  },
];

type BoomerangState = {
  candidates: BoomerangCandidate[];
  selectedCandidateId: string;
  strategy: BoomerangResolutionStrategy;
  reason: string;
  moveGrants: boolean;
  continueVesting: boolean;
  error?: string;
};

type BoomerangAction =
  | { type: "candidatesFound"; candidates: BoomerangCandidate[] }
  | { type: "candidateSelected"; candidateId: string }
  | { type: "strategyChanged"; strategy: BoomerangResolutionStrategy }
  | { type: "reasonChanged"; reason: string }
  | { type: "moveGrantsChanged"; moveGrants: boolean }
  | { type: "continueVestingChanged"; continueVesting: boolean }
  | { type: "errorChanged"; error?: string };

const initialBoomerangState: BoomerangState = {
  candidates: [],
  selectedCandidateId: "",
  strategy: "NOT_BOOMERANG",
  reason: "",
  moveGrants: false,
  continueVesting: false,
};

function boomerangReducer(
  state: BoomerangState,
  action: BoomerangAction
): BoomerangState {
  switch (action.type) {
    case "candidatesFound":
      return {
        ...state,
        candidates: action.candidates,
        selectedCandidateId: action.candidates[0]?.employeeId || "",
        strategy: "NOT_BOOMERANG",
        moveGrants: false,
        continueVesting: false,
        error: undefined,
      };
    case "candidateSelected":
      return {
        ...state,
        selectedCandidateId: action.candidateId,
        strategy: "NOT_BOOMERANG",
        moveGrants: false,
        continueVesting: false,
        error: undefined,
      };
    case "strategyChanged":
      return {
        ...state,
        strategy: action.strategy,
        moveGrants: false,
        continueVesting: false,
        error: undefined,
      };
    case "reasonChanged":
      return { ...state, reason: action.reason };
    case "moveGrantsChanged":
      return {
        ...state,
        moveGrants: action.moveGrants,
        continueVesting: action.moveGrants ? state.continueVesting : false,
      };
    case "continueVestingChanged":
      return { ...state, continueVesting: action.continueVesting };
    case "errorChanged":
      return { ...state, error: action.error };
    default:
      return state;
  }
}

function formatCandidateDate(value?: string) {
  if (!value) return "Not recorded";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
}

function BoomerangResolutionSection({
  headingRef,
  candidates,
  selectedCandidateId,
  strategy,
  reason,
  moveGrants,
  continueVesting,
  error,
  onCandidateChange,
  onStrategyChange,
  onReasonChange,
  onMoveGrantsChange,
  onContinueVestingChange,
  onReviewLatest,
}: {
  headingRef: RefObject<HTMLHeadingElement>;
  candidates: BoomerangCandidate[];
  selectedCandidateId: string;
  strategy: BoomerangResolutionStrategy;
  reason: string;
  moveGrants: boolean;
  continueVesting: boolean;
  error?: string;
  onCandidateChange: (value: string) => void;
  onStrategyChange: (value: BoomerangResolutionStrategy) => void;
  onReasonChange: (value: string) => void;
  onMoveGrantsChange: (value: boolean) => void;
  onContinueVestingChange: (value: boolean) => void;
  onReviewLatest: () => void;
}) {
  const selectedCandidate = candidates.find(
    (candidate) => candidate.employeeId === selectedCandidateId
  );
  return (
    <Card
      id="boomerang"
      className="scroll-mt-6 border-borderColor bg-white shadow-card"
    >
      <CardHeader className="gap-1 border-b border-borderColor p-4 sm:p-6">
        <CardTitle
          ref={headingRef}
          tabIndex={-1}
          className="text-base leading-6 text-headerColor focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          Former employee found
        </CardTitle>
        <p className="text-sm text-secondaryText">
          This profile may belong to someone who previously worked here. Review
          the previous employment before creating the employee.
        </p>
      </CardHeader>
      <CardContent className="p-4 sm:p-6">
        <VStack className="gap-6">
          {error && (
            <HStack
              className="w-full flex-col items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 p-4 sm:flex-row sm:items-center"
              role="alert"
            >
              <HStack className="items-start gap-2">
                <Icon
                  icon="material-symbols:error-outline-rounded"
                  className="mt-0.5 shrink-0 text-red-600"
                  width={18}
                />
                <p className="text-sm text-red-700">{error}</p>
              </HStack>
              <ButtonSecondary type="button" onClick={onReviewLatest}>
                Review latest match
              </ButtonSecondary>
            </HStack>
          )}

          <VStack className="gap-2">
            <Label>Select the former employment</Label>
            <RadioGroup
              name="boomerangCandidate"
              value={selectedCandidateId}
              onValueChange={onCandidateChange}
              className="gap-3"
            >
              {candidates.map((candidate) => (
                <label
                  key={candidate.employeeId}
                  className={`flex items-start gap-3 rounded-lg border-2 bg-white p-4 transition-colors ${
                    selectedCandidateId === candidate.employeeId
                      ? "border-orange-501"
                      : "border-gray-200"
                  } cursor-pointer hover:border-inputBorderFocus`}
                >
                  <RadioGroupItem
                    value={candidate.employeeId}
                    aria-label={`Select ${candidate.employeeName}`}
                  />
                  <VStack className="min-w-0 flex-1 gap-2">
                    <HStack className="w-full flex-wrap items-start justify-between gap-2">
                      <VStack className="gap-1">
                        <p className="text-sm font-semibold text-headerColor">
                          {candidate.employeeName}
                        </p>
                        <p className="text-xs text-secondaryText">
                          {candidate.employeeIdentification} ·{" "}
                          {candidate.workEmail}
                        </p>
                      </VStack>
                      {candidate.alreadyLinked && (
                        <span className="rounded-lg bg-tertiaryBg px-2 py-1 text-xs font-medium text-primaryText">
                          Already linked
                        </span>
                      )}
                    </HStack>
                    <HStack className="flex-wrap items-center gap-2">
                      {candidate.matchReasons.map((matchReason) => (
                        <span
                          key={matchReason}
                          className="rounded-lg bg-tertiaryBg px-2 py-1 text-xs text-primaryText"
                        >
                          {MATCH_REASON_LABELS[matchReason]}
                        </span>
                      ))}
                    </HStack>
                    <div className="grid grid-cols-1 gap-3 text-xs text-secondaryText sm:grid-cols-3">
                      <p>
                        Designation:{" "}
                        {candidate.previousDesignation || "Not recorded"}
                      </p>
                      <p>
                        Last working day:{" "}
                        {formatCandidateDate(candidate.lastWorkingDay)}
                      </p>
                      <p>
                        Resigned on:{" "}
                        {formatCandidateDate(candidate.resignationDate)}
                      </p>
                      <p>
                        Portal access:{" "}
                        {candidate.hasPortalAccess ? "Yes" : "No"}
                      </p>
                      <p>Former grants: {candidate.grantCount}</p>
                    </div>
                  </VStack>
                </label>
              ))}
            </RadioGroup>
          </VStack>

          {selectedCandidate && (
            <VStack className="gap-2">
              <Label>Choose how to create this employee</Label>
              <RadioGroup
                name="boomerangStrategy"
                value={strategy}
                onValueChange={(value) =>
                  onStrategyChange(value as BoomerangResolutionStrategy)
                }
                className="gap-3"
              >
                {RESOLUTION_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className={`flex items-start gap-3 rounded-lg border-2 bg-white p-4 transition-colors ${
                      strategy === option.value
                        ? "border-orange-501"
                        : "border-gray-200 hover:border-inputBorderFocus"
                    } ${
                      option.value === "CREATE_SEPARATE_EMPLOYEE" &&
                      selectedCandidate.alreadyLinked
                        ? "cursor-not-allowed opacity-60"
                        : "cursor-pointer"
                    }`}
                  >
                    <RadioGroupItem
                      value={option.value}
                      disabled={
                        option.value === "CREATE_SEPARATE_EMPLOYEE" &&
                        selectedCandidate.alreadyLinked
                      }
                    />
                    <VStack className="gap-1">
                      <p className="text-sm font-medium text-headerColor">
                        {option.title}
                      </p>
                      <p className="text-xs text-secondaryText">
                        {option.description}
                      </p>
                    </VStack>
                  </label>
                ))}
              </RadioGroup>
            </VStack>
          )}

          {strategy === "MERGE_EXISTING_EMPLOYEE" && (
            <VStack className="gap-2">
              <Label>Should grant vesting continue?</Label>
              <RadioGroup
                name="continueMergeVesting"
                value={continueVesting ? "yes" : "no"}
                onValueChange={(value) =>
                  onContinueVestingChange(value === "yes")
                }
                className="grid gap-3 sm:grid-cols-2"
              >
                {[
                  ["no", "No, keep the current grant state"],
                  ["yes", "Yes, restore eligible lapsed vesting"],
                ].map(([value, label]) => (
                  <label
                    key={value}
                    className={`flex cursor-pointer items-start gap-3 rounded-lg border-2 bg-white p-4 ${
                      (continueVesting ? "yes" : "no") === value
                        ? "border-orange-501"
                        : "border-gray-200"
                    }`}
                  >
                    <RadioGroupItem value={value} />
                    <span className="text-sm font-medium text-headerColor">
                      {label}
                    </span>
                  </label>
                ))}
              </RadioGroup>
            </VStack>
          )}

          {strategy === "CREATE_SEPARATE_EMPLOYEE" && (
            <VStack className="gap-4">
              <VStack className="gap-2">
                <Label>Move former grants to the new employee?</Label>
                <RadioGroup
                  name="moveBoomerangGrants"
                  value={moveGrants ? "yes" : "no"}
                  onValueChange={(value) => onMoveGrantsChange(value === "yes")}
                  className="grid gap-3 sm:grid-cols-2"
                >
                  {[
                    ["no", "No, keep grants on the former employee"],
                    ["yes", "Yes, move all grants to the new employee"],
                  ].map(([value, label]) => (
                    <label
                      key={value}
                      className={`flex cursor-pointer items-start gap-3 rounded-lg border-2 bg-white p-4 ${
                        (moveGrants ? "yes" : "no") === value
                          ? "border-orange-501"
                          : "border-gray-200"
                      }`}
                    >
                      <RadioGroupItem value={value} />
                      <span className="text-sm font-medium text-headerColor">
                        {label}
                      </span>
                    </label>
                  ))}
                </RadioGroup>
              </VStack>
              {moveGrants && (
                <VStack className="gap-2">
                  <Label>Should grant vesting continue?</Label>
                  <RadioGroup
                    name="continueSeparateVesting"
                    value={continueVesting ? "yes" : "no"}
                    onValueChange={(value) =>
                      onContinueVestingChange(value === "yes")
                    }
                    className="grid gap-3 sm:grid-cols-2"
                  >
                    {[
                      ["no", "No, preserve current vesting states"],
                      ["yes", "Yes, restore eligible lapsed vesting"],
                    ].map(([value, label]) => (
                      <label
                        key={value}
                        className={`flex cursor-pointer items-start gap-3 rounded-lg border-2 bg-white p-4 ${
                          (continueVesting ? "yes" : "no") === value
                            ? "border-orange-501"
                            : "border-gray-200"
                        }`}
                      >
                        <RadioGroupItem value={value} />
                        <span className="text-sm font-medium text-headerColor">
                          {label}
                        </span>
                      </label>
                    ))}
                  </RadioGroup>
                </VStack>
              )}
            </VStack>
          )}

          {selectedCandidate && strategy !== "NOT_BOOMERANG" && (
            <Box className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="text-sm text-secondaryText">
                {selectedCandidate.grantCount} former grant(s), with{" "}
                {selectedCandidate.reversibleLapsedVestingCount} potentially
                reversible lapsed vesting tranche(s).
              </p>
            </Box>
          )}

          <FieldBlock
            label="Reason"
            hint="Optional note for the employee activity log"
          >
            <TextArea
              id="boomerang-reason"
              name="boomerangReason"
              value={reason}
              onChange={(event) => onReasonChange(event.target.value)}
            />
          </FieldBlock>
        </VStack>
      </CardContent>
    </Card>
  );
}

function FormSection({
  id,
  icon,
  title,
  description,
  children,
}: {
  id: string;
  icon: string;
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <section
      id={id}
      className="scroll-mt-6 overflow-hidden rounded-lg border border-borderColor bg-white drop-shadow-box"
    >
      <div className="flex items-start gap-3 border-b border-borderColor px-5 py-4 sm:px-6">
        <div>
          <h2 className="text-lg1 font-semibold text-black-501">{title}</h2>
          <p className="mt-0.5 text-xs text-gray-light">{description}</p>
        </div>
      </div>
      <div className="p-5 sm:p-6">{children}</div>
    </section>
  );
}

const profileSections: SectionStep[] = [
  {
    id: "identity",
    title: "Identity",
    description: "Employee identity",
  },
  {
    id: "professional",
    title: "Professional",
    description: "Role and reporting",
  },
  {
    id: "personal",
    title: "Personal",
    description: "Contact and statutory",
  },
  {
    id: "nominees",
    title: "Nominees",
    description: "Nomination details",
  },
  {
    id: "demat",
    title: "Demat accounts",
    description: "Account information",
  },
  {
    id: "custom",
    title: "Custom fields",
    description: "Company-specific data",
  },
];

function toEditValues(detail: any): AddEmployeeReq {
  return {
    ...emptyValues,
    id: detail.id,
    employeeName: detail.employeeName || "",
    legalName: detail.legalName || detail.employeeName || "",
    email: detail.emailAddress || "",
    personalEmail: detail.personalEmail || "",
    employeeIdentification: detail.employeeIdentification || "",
    employmentType: detail.employmentType || EmployementType.EMPLOYEE,
    dateOfJoin: detail.dateOfJoin ? formatDate(detail.dateOfJoin) : "",
    subsidiaryCompanyId: detail.subsidiaryCompanyId || "",
    employeeDesignation: detail.designation || "",
    department: detail.department || "",
    managerIds: detail.managers || [],
    grade: detail.grade || "",
    salary: detail.salary || undefined,
    contactNumber: detail.contactNumber || "",
    address: detail.address || "",
    residentialStatus: detail.residentStatus || "India",
    nationality: detail.nationality || "India",
    notes: detail.notes || "",
    tags: normalizeEmployeeTags(detail.additionalData?.tags),
    dematAccounts: detail.dematAccounts || [],
    nominees: (detail.nominees || []).map((nominee: any) => ({
      ...nominee,
      dateOfBirth: nominee.dateOfBirth ? formatDate(nominee.dateOfBirth) : "",
      guardian: nominee.guardian
        ? {
            ...nominee.guardian,
            percentage: 0,
            dateOfBirth: nominee.guardian.dateOfBirth
              ? formatDate(nominee.guardian.dateOfBirth)
              : "",
          }
        : undefined,
    })),
    imageURL: detail.imageURL || "",
    pan: "",
    employmentStatus: detail.status,
    lastDay: detail.lastDay ? formatDate(detail.lastDay) : undefined,
  };
}

export default function EmployeeFormPage() {
  const { employeeId } = useParams();
  const isEdit = Boolean(employeeId);
  const navigate = useNavigate();
  const location = useLocation();
  const { data: detail, isLoading: loadingDetail } = useEmployeePageDetails(
    employeeId || "",
    { enabled: isEdit }
  );
  const { data: subsidiaries = [] } = useSubsidiaryCompanyNames();
  const { data: companyManagers = [] } = useCompanyManagers();
  const { data: employeeCustomFields = [] } = useGetCustomFields(
    UdfEntityType.EMPLOYEE
  );
  const { mutateAsync: createEmployee, isLoading: creating } = useAddEmployee();
  const { mutateAsync: updateEmployee, isLoading: updating } =
    usePatchEmployee();
  const [customFields, setCustomFields] = useState<Record<string, any>>({});
  const [initialCustomFields, setInitialCustomFields] = useState<
    Record<string, any>
  >({});
  const [profileImageChange, setProfileImageChange] = useState<any>();
  const [hasConcurrentUpdate, setHasConcurrentUpdate] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState(currentLocalDate);
  const [activeSection, setActiveSection] = useState("identity");
  const [boomerang, dispatchBoomerang] = useReducer(
    boomerangReducer,
    initialBoomerangState
  );
  const boomerangHeadingRef = useRef<HTMLHeadingElement>(null);
  const visibleProfileSections = useMemo(() => {
    const sections = employeeCustomFields.length
      ? profileSections
      : profileSections.filter((section) => section.id !== "custom");
    const editSections = isEdit
      ? [
          ...sections,
          {
            id: "effective-date",
            title: "Effective date",
            description: "Date used for this employee history event",
          },
        ]
      : sections;
    return boomerang.candidates.length
      ? [
          ...editSections,
          {
            id: "boomerang",
            title: "Former employment",
            description: "Rejoin resolution",
          },
        ]
      : editSections;
  }, [boomerang.candidates.length, employeeCustomFields.length, isEdit]);

  useEffect(() => {
    if (!detail) return;
    const values = detail.additionalData?.udfValues || {};
    setCustomFields(values);
    setInitialCustomFields(values);
  }, [detail]);

  useEffect(() => {
    if (loadingDetail) return undefined;
    const sections = visibleProfileSections
      .map((section) => document.getElementById(String(section.id)))
      .filter((section): section is HTMLElement => Boolean(section));
    if (!sections.length) return undefined;

    const updateActiveSection = () => {
      const marker = Math.min(window.innerHeight * 0.3, 240);
      let current = sections[0].id;
      for (const section of sections) {
        if (section.getBoundingClientRect().top <= marker) {
          current = section.id;
        } else {
          break;
        }
      }
      setActiveSection(current);
    };

    updateActiveSection();
    window.addEventListener("scroll", updateActiveSection, { passive: true });
    document.addEventListener("scroll", updateActiveSection, {
      passive: true,
      capture: true,
    });
    return () => {
      window.removeEventListener("scroll", updateActiveSection);
      document.removeEventListener("scroll", updateActiveSection, true);
    };
  }, [loadingDetail, visibleProfileSections]);

  useEffect(() => {
    if (!boomerang.candidates.length) return;
    boomerangHeadingRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
    boomerangHeadingRef.current?.focus();
  }, [boomerang.candidates.length]);

  const initialValues = useMemo(
    () => (isEdit && detail ? toEditValues(detail) : emptyValues),
    [detail, isEdit]
  );
  const managerOptions = (
    Array.isArray(companyManagers) ? companyManagers : []
  ).map((user: any) => ({
    id: String(user.userId),
    name: user.fullName || user.emailId,
  }));
  const returnTo = (location.state as any)?.returnTo || "/options/allEmployees";
  const scrollToProfileSection = (sectionId: string) => {
    setActiveSection(sectionId);
    document.getElementById(sectionId)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  if (isEdit && loadingDetail) {
    return (
      <VStack className="gap-5">
        <BCHeader
          className="items-baseline py-1"
          bcTitle="Stakeholders"
          bcSubTitle="Edit Profile"
          onClick={() => navigate(returnTo)}
        />
        <div className="h-72 rounded-lg border border-borderColor bg-white loading" />
      </VStack>
    );
  }

  return (
    <VStack className="gap-5">
      <BCHeader
        className="items-baseline py-1"
        bcTitle="Stakeholders"
        bcSubTitle={isEdit ? "Edit Profile" : "Add New Option Holder"}
        onClick={() => navigate(returnTo)}
      />

      <Formik
        enableReinitialize
        validateOnMount
        initialValues={initialValues}
        validationSchema={requiredSchema}
        validate={(values) =>
          validateEmployeeStandardFields(values).reduce<Record<string, string>>(
            (errors, { field, message }) => setIn(errors, field, message),
            {}
          )
        }
        onSubmit={async (values, formikHelpers) => {
          try {
            if (isEdit && detail) {
              const panCipher = values.pan
                ? await encryptPii(values.pan)
                : undefined;
              const changes = buildEmployeePatch(initialValues, values, {
                customFieldInitial: initialCustomFields,
                customFieldCurrent: customFields,
                panCipher,
                profileImage: profileImageChange,
              });
              if (!Object.keys(changes).length) {
                toast("No profile changes to save", { type: "info" });
                return;
              }
              await updateEmployee({
                employeeId: employeeId!,
                request: {
                  expectedUpdatedAt: detail.updatedAt,
                  effectiveDate,
                  changes,
                },
              });
              toast("Employee profile updated", { type: "success" });
              navigate(`/options/employeeDetails/${employeeId}`);
            } else {
              const request: any = {
                ...values,
                email: values.email.trim(),
                personalEmail: values.personalEmail?.trim(),
                dateOfJoin: formatWithTimeZone(values.dateOfJoin),
                customFieldValues: customFields,
              };
              const selectedCandidate = boomerang.candidates.find(
                (candidate) =>
                  candidate.employeeId === boomerang.selectedCandidateId
              );
              if (selectedCandidate) {
                const baseResolution = {
                  candidateEmployeeId: selectedCandidate.employeeId,
                  strategy: boomerang.strategy,
                  expectedCandidateUpdatedAt: selectedCandidate.updatedAt,
                  ...(boomerang.reason.trim()
                    ? { reason: boomerang.reason.trim() }
                    : {}),
                };
                request.boomerangResolution =
                  boomerang.strategy === "MERGE_EXISTING_EMPLOYEE"
                    ? {
                        ...baseResolution,
                        continueVesting: boomerang.continueVesting,
                      }
                    : boomerang.strategy === "CREATE_SEPARATE_EMPLOYEE"
                    ? {
                        ...baseResolution,
                        moveGrants: boomerang.moveGrants,
                        continueVesting:
                          boomerang.moveGrants && boomerang.continueVesting,
                      }
                    : baseResolution;
              }
              if (values.pan) {
                request.panCipher = await encryptPii(values.pan);
                delete request.pan;
              }
              if (profileImageChange?.operation === "REPLACE") {
                request.imageURL = profileImageChange.dataUrl;
              }
              await createEmployee(request);
              toast(
                selectedCandidate && boomerang.strategy !== "NOT_BOOMERANG"
                  ? "Rejoin profile created successfully"
                  : "Employee added successfully",
                { type: "success" }
              );
              navigate("/options/allEmployees");
            }
          } catch (error: any) {
            const rawError = error?.response?.data?.errorRaw;
            const code =
              typeof rawError === "string" ? rawError : rawError?.reason;
            const conflictFields: string[] = Array.isArray(rawError?.fields)
              ? rawError.fields
              : [];
            if (code === "BOOMERANG_CANDIDATE") {
              const candidates = (rawError?.candidates ||
                []) as BoomerangCandidate[];
              dispatchBoomerang({ type: "candidatesFound", candidates });
              return;
            }
            if (code?.startsWith("BOOMERANG_")) {
              const message =
                error?.response?.data?.errorMessage ||
                "Review the former employee details and try again";
              dispatchBoomerang({ type: "errorChanged", error: message });
              if (code === "BOOMERANG_EMAIL_CONFLICT") {
                const emailFields = conflictFields.length
                  ? conflictFields
                  : ["email", "personalEmail"];
                if (emailFields.includes("email")) {
                  formikHelpers.setFieldError(
                    "email",
                    "Use a work email that is not assigned to another employee."
                  );
                  formikHelpers.setFieldTouched("email", true, false);
                }
                if (emailFields.includes("personalEmail")) {
                  formikHelpers.setFieldError(
                    "personalEmail",
                    "Use a personal email that is not assigned to another employee."
                  );
                  formikHelpers.setFieldTouched("personalEmail", true, false);
                }
                document
                  .querySelector<HTMLInputElement>(
                    `input[name="${emailFields[0] || "email"}"]`
                  )
                  ?.focus();
              }
              if (code === "BOOMERANG_EMPLOYEE_ID_CONFLICT") {
                formikHelpers.setFieldError(
                  "employeeIdentification",
                  "This employee ID is already used by another employee. Enter a unique employee ID."
                );
                formikHelpers.setFieldTouched(
                  "employeeIdentification",
                  true,
                  false
                );
                document
                  .querySelector<HTMLInputElement>(
                    'input[name="employeeIdentification"]'
                  )
                  ?.focus();
              }
              return;
            }
            if (code === "EMPLOYEE_CHANGED") setHasConcurrentUpdate(true);
            toast(
              code === "EMPLOYEE_CHANGED"
                ? "This employee changed in another session. Reload the page before saving."
                : error?.response?.data?.errorMessage ||
                    "Unable to save employee",
              { type: "error" }
            );
          }
        }}
      >
        {(formik) => {
          const hasEffectiveChanges =
            Object.keys(
              buildEmployeePatch(initialValues, formik.values, {
                customFieldInitial: initialCustomFields,
                customFieldCurrent: customFields,
                profileImage: profileImageChange,
              })
            ).length > 0 || Boolean(formik.values.pan?.trim());

          return (
            <Form className="space-y-5">
              {hasConcurrentUpdate && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <p className="text-sm text-amber-900">
                    A newer version of this profile exists. Your edits are still
                    here; reload only when you are ready to review the latest
                    data.
                  </p>
                  <ButtonSecondary
                    type="button"
                    onClick={() => window.location.reload()}
                  >
                    Reload latest
                  </ButtonSecondary>
                </div>
              )}
              <div className="lg:hidden">
                <FieldBlock label="Profile section">
                  <Select
                    options={visibleProfileSections.map((section) => ({
                      value: String(section.id),
                      label: section.title,
                    }))}
                    valueGetter={(option) => option.value}
                    textGetter={(option) => option.label}
                    value={activeSection}
                    isPlaceHolderDisabled
                    onChange={(event) => {
                      scrollToProfileSection(event.target.value);
                    }}
                  />
                </FieldBlock>
              </div>
              <div className="grid items-start gap-5 lg:grid-cols-4">
                <aside className="hidden rounded-lg border border-borderColor bg-white pb-6 drop-shadow-box lg:sticky lg:top-5 lg:block">
                  <SectionStepper
                    steps={visibleProfileSections}
                    activeStepId={activeSection}
                    onStepClick={(step) => scrollToProfileSection(step.id)}
                  />
                </aside>
                <div className="min-w-0 space-y-5 lg:col-span-3">
                  <FormSection
                    id="identity"
                    icon="material-symbols:badge-outline-rounded"
                    title="Identity"
                    description="Basic information used to identify this employee."
                  >
                    <div className="grid md:grid-cols-2 gap-5">
                      <FieldBlock
                        label="Employee name"
                        required
                        error={
                          formik.touched.employeeName &&
                          formik.errors.employeeName
                        }
                      >
                        <Input
                          placeholder="e.g. Sarah Jenkins"
                          {...formik.getFieldProps("employeeName")}
                        />
                      </FieldBlock>
                      <FieldBlock
                        label="Legal name"
                        error={
                          formik.touched.legalName && formik.errors.legalName
                        }
                      >
                        <Input
                          placeholder="Name as per legal records"
                          {...formik.getFieldProps("legalName")}
                        />
                      </FieldBlock>
                      <FieldBlock
                        label="Work email"
                        required
                        error={formik.touched.email && formik.errors.email}
                      >
                        <Input
                          placeholder="name@company.com"
                          type="email"
                          {...formik.getFieldProps("email")}
                          onChange={(event) => {
                            formik.handleChange(event);
                            dispatchBoomerang({ type: "errorChanged" });
                          }}
                        />
                      </FieldBlock>
                      <FieldBlock
                        label="Personal email"
                        error={
                          formik.touched.personalEmail &&
                          formik.errors.personalEmail
                        }
                      >
                        <Input
                          placeholder="name@example.com"
                          type="email"
                          {...formik.getFieldProps("personalEmail")}
                          onChange={(event) => {
                            formik.handleChange(event);
                            dispatchBoomerang({ type: "errorChanged" });
                          }}
                        />
                      </FieldBlock>
                      <FieldBlock
                        label="Employee ID"
                        required
                        error={
                          formik.touched.employeeIdentification &&
                          formik.errors.employeeIdentification
                        }
                      >
                        <Input
                          placeholder="e.g. EMP-1024"
                          {...formik.getFieldProps("employeeIdentification")}
                          onChange={(event) => {
                            formik.handleChange(event);
                            dispatchBoomerang({ type: "errorChanged" });
                          }}
                        />
                      </FieldBlock>
                      <FieldBlock label="Employee type" required>
                        <Select
                          options={[
                            { value: "EMPLOYEE", label: "Employee" },
                            { value: "CONSULTANT", label: "Consultant" },
                          ]}
                          valueGetter={(option) => option.value}
                          textGetter={(option) => option.label}
                          isPlaceHolderDisabled
                          {...formik.getFieldProps("employmentType")}
                        />
                      </FieldBlock>
                      <FieldBlock
                        label="Date of joining"
                        required
                        error={
                          formik.touched.dateOfJoin && formik.errors.dateOfJoin
                        }
                      >
                        <Input
                          type="date"
                          {...formik.getFieldProps("dateOfJoin")}
                        />
                      </FieldBlock>
                      {!isEdit && (
                        <FieldBlock label="Employment status">
                          <Select
                            options={[
                              { value: "EMPLOYED", label: "Employed" },
                              { value: "RESIGNED", label: "Resigned" },
                            ]}
                            valueGetter={(option) => option.value}
                            textGetter={(option) => option.label}
                            isPlaceHolderDisabled
                            {...formik.getFieldProps("employmentStatus")}
                          />
                        </FieldBlock>
                      )}
                      {!isEdit &&
                        formik.values.employmentStatus ===
                          EmployementStatus.RESIGNED && (
                          <FieldBlock
                            label="Last working day"
                            required
                            error={
                              formik.touched.lastDay && formik.errors.lastDay
                            }
                          >
                            <Input
                              type="date"
                              {...formik.getFieldProps("lastDay")}
                            />
                          </FieldBlock>
                        )}
                    </div>
                  </FormSection>

                  <FormSection
                    id="professional"
                    icon="material-symbols:business-center-outline-rounded"
                    title="Professional details"
                    description="Role, organization, reporting, and compensation information."
                  >
                    <div className="grid md:grid-cols-2 gap-5">
                      <FieldBlock label="Organization">
                        <Select
                          options={subsidiaries}
                          placeholder="Primary organization"
                          valueGetter={(option: any) => option.id}
                          textGetter={(option: any) => option.name}
                          {...formik.getFieldProps("subsidiaryCompanyId")}
                        />
                      </FieldBlock>
                      <FieldBlock
                        label="Designation"
                        required
                        error={
                          formik.touched.employeeDesignation &&
                          formik.errors.employeeDesignation
                        }
                      >
                        <Input
                          placeholder="e.g. Senior Software Engineer"
                          {...formik.getFieldProps("employeeDesignation")}
                        />
                      </FieldBlock>
                      <FieldBlock
                        label="Department"
                        required
                        error={
                          formik.touched.department && formik.errors.department
                        }
                      >
                        <Input
                          placeholder="e.g. Engineering"
                          {...formik.getFieldProps("department")}
                        />
                      </FieldBlock>
                      <FieldBlock
                        label="Managers"
                        hint="Search and select one or more managers."
                      >
                        <ReactSelect
                          isMulti
                          closeMenuOnSelect={false}
                          classNamePrefix="employee-select"
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 50 }),
                          }}
                          placeholder="Search managers…"
                          options={managerOptions.map((manager) => ({
                            value: manager.id,
                            label: manager.name,
                          }))}
                          value={managerOptions
                            .filter((manager) =>
                              (formik.values.managerIds || []).includes(
                                manager.id
                              )
                            )
                            .map((manager) => ({
                              value: manager.id,
                              label: manager.name,
                            }))}
                          onChange={(value) =>
                            formik.setFieldValue(
                              "managerIds",
                              value.map((item) => item.value)
                            )
                          }
                        />
                      </FieldBlock>
                      <FieldBlock
                        label="Grade"
                        error={formik.touched.grade && formik.errors.grade}
                      >
                        <Input
                          placeholder="e.g. L4"
                          {...formik.getFieldProps("grade")}
                        />
                      </FieldBlock>
                      <FieldBlock label="Salary">
                        <Input
                          placeholder="Taxable salary"
                          type="number"
                          min="0"
                          {...formik.getFieldProps("salary")}
                        />
                      </FieldBlock>
                      <div className="md:col-span-2">
                        <FieldBlock
                          label="Notes"
                          hint="Optional internal notes about the employee."
                          error={formik.touched.notes && formik.errors.notes}
                        >
                          <TextArea
                            className="min-h-[104px] resize-y"
                            placeholder="Add notes…"
                            {...formik.getFieldProps("notes")}
                          />
                        </FieldBlock>
                      </div>
                    </div>
                  </FormSection>

                  <FormSection
                    id="personal"
                    icon="material-symbols:person-outline-rounded"
                    title="Personal details"
                    description="Contact, statutory, and personal information."
                  >
                    <div className="grid md:grid-cols-2 gap-5">
                      <FieldBlock
                        label="Contact number"
                        error={
                          formik.touched.contactNumber &&
                          formik.errors.contactNumber
                        }
                      >
                        <Input
                          id="contactNumber"
                          type="tel"
                          inputMode="numeric"
                          pattern="[0-9]{10}"
                          maxLength={10}
                          placeholder="Phone number"
                          {...formik.getFieldProps("contactNumber")}
                          onChange={(event) => {
                            formik.setFieldValue(
                              "contactNumber",
                              event.target.value.replace(/\D/g, "").slice(0, 10)
                            );
                          }}
                        />
                      </FieldBlock>
                      <FieldBlock
                        label="PAN"
                        error={formik.touched.pan && formik.errors.pan}
                      >
                        <Input
                          placeholder="Enter PAN number"
                          {...formik.getFieldProps("pan")}
                        />
                      </FieldBlock>
                      <FieldBlock
                        label="Country of residence"
                        required
                        error={
                          formik.touched.residentialStatus &&
                          formik.errors.residentialStatus
                        }
                      >
                        <Select
                          options={getNames().sort()}
                          isPlaceHolderDisabled
                          {...formik.getFieldProps("residentialStatus")}
                        />
                      </FieldBlock>
                      <FieldBlock
                        label="Nationality"
                        required
                        error={
                          formik.touched.nationality &&
                          formik.errors.nationality
                        }
                      >
                        <Select
                          options={getNames().sort()}
                          isPlaceHolderDisabled
                          {...formik.getFieldProps("nationality")}
                        />
                      </FieldBlock>
                      <div className="md:col-span-2">
                        <FieldBlock
                          label="Address"
                          error={
                            formik.touched.address && formik.errors.address
                          }
                        >
                          <TextArea
                            className="min-h-[104px] resize-y"
                            placeholder="Residential address"
                            {...formik.getFieldProps("address")}
                          />
                        </FieldBlock>
                      </div>
                      <FieldBlock
                        label="Profile photo"
                        hint="PNG or JPG. Use a square image for the best result."
                      >
                        <div className="flex items-center gap-4 rounded-lg border border-dashed border-inputBorder bg-slate-light p-4">
                          {formik.values.imageURL ? (
                            <img
                              src={formik.values.imageURL}
                              alt="Profile preview"
                              className="h-16 w-16 rounded-lg object-cover"
                            />
                          ) : (
                            <span className="flex h-16 w-16 items-center justify-center rounded-lg bg-white text-gray-light shadow-box">
                              <Icon
                                icon="material-symbols:person-rounded"
                                width={28}
                              />
                            </span>
                          )}
                          <div className="flex flex-wrap gap-2">
                            <label
                              htmlFor="employee-profile-photo"
                              className="cursor-pointer rounded-sm border border-inputBorder bg-white px-3 py-2 text-sm font-semibold text-gray-dark shadow-box hover:bg-tertiaryBg"
                            >
                              Choose photo
                            </label>
                            <input
                              id="employee-profile-photo"
                              className="hidden"
                              type="file"
                              accept="image/png,image/jpeg"
                              onChange={async (event) => {
                                const file = event.target.files?.[0];
                                if (!file) return;
                                if (file.size > 5 * 1024 * 1024) {
                                  toast(
                                    "Profile photo must be smaller than 5 MB",
                                    {
                                      type: "error",
                                    }
                                  );
                                  event.target.value = "";
                                  return;
                                }
                                const dataUrl = (await convertToBase64(
                                  file,
                                  true
                                )) as string;
                                setProfileImageChange({
                                  operation: "REPLACE",
                                  dataUrl,
                                });
                                formik.setFieldValue("imageURL", dataUrl);
                              }}
                            />
                            {formik.values.imageURL ? (
                              <IconCTAButton
                                type="button"
                                iconName="material-symbols:delete-outline-rounded"
                                iconSize={IconSize.M}
                                iconOnly
                                destructive
                                aria-label="Remove profile photo"
                                title="Remove profile photo"
                                onClick={() => {
                                  setProfileImageChange({
                                    operation: "REMOVE",
                                  });
                                  formik.setFieldValue("imageURL", "");
                                }}
                              />
                            ) : null}
                          </div>
                        </div>
                      </FieldBlock>
                      <FieldBlock
                        label="Tags"
                        hint="Type a tag and press Enter to create it."
                      >
                        <CreatableSelect
                          isMulti
                          classNamePrefix="employee-select"
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 50 }),
                          }}
                          placeholder="Add tags…"
                          options={employeeTagOptions}
                          value={formik.values.tags || []}
                          onChange={(value) =>
                            formik.setFieldValue(
                              "tags",
                              normalizeEmployeeTags(value)
                            )
                          }
                        />
                      </FieldBlock>
                    </div>
                  </FormSection>

                  <FormSection
                    id="nominees"
                    icon="material-symbols:group-outline-rounded"
                    title="Nominees"
                    description="Record beneficiaries and allocation details. A guardian is required for a minor nominee."
                  >
                    <NomineesEditor formik={formik} />
                  </FormSection>

                  <FormSection
                    id="demat"
                    icon="material-symbols:account-balance-wallet-outline-rounded"
                    title="Demat accounts"
                    description="Add account identifiers and optional CML documents."
                  >
                    <div className="mb-5 flex justify-end">
                      <ButtonSecondary
                        type="button"
                        onClick={() =>
                          formik.setFieldValue("dematAccounts", [
                            ...(formik.values.dematAccounts || []),
                            {
                              dematAccountId: "",
                              type: "NSDL",
                              status: "active",
                              isUserUploaded: false,
                            },
                          ])
                        }
                      >
                        <span className="inline-flex items-center gap-1.5">
                          <Icon
                            icon="material-symbols:add-rounded"
                            width={18}
                          />
                          Add account
                        </span>
                      </ButtonSecondary>
                    </div>
                    <div className="space-y-4">
                      {(formik.values.dematAccounts || []).map(
                        (account: DematAccount, index) => (
                          <div
                            key={account.id || index}
                            className="rounded-lg border border-borderColor bg-white p-4"
                          >
                            <div className="mb-3 flex items-center justify-between">
                              <p className="text-sm font-medium text-gray-dark">
                                Account {index + 1}
                              </p>
                              <button
                                type="button"
                                aria-label={`Remove account ${index + 1}`}
                                className="flex h-8 w-8 items-center justify-center rounded-md text-gray-light transition hover:bg-red-50 hover:text-red-600"
                                onClick={() =>
                                  formik.setFieldValue(
                                    "dematAccounts",
                                    (formik.values.dematAccounts || []).filter(
                                      (_, i) => i !== index
                                    )
                                  )
                                }
                              >
                                <Icon
                                  icon="material-symbols:delete-outline-rounded"
                                  width={19}
                                />
                              </button>
                            </div>
                            <div className="grid gap-4 md:grid-cols-2">
                              <FieldBlock
                                label="Account ID"
                                required
                                error={
                                  ((formik.touched.dematAccounts as any)?.[
                                    index
                                  ]?.dematAccountId ||
                                    formik.submitCount > 0) &&
                                  (formik.errors.dematAccounts as any)?.[index]
                                    ?.dematAccountId
                                }
                              >
                                <Input
                                  placeholder="Enter 16-character ID"
                                  value={account.dematAccountId}
                                  onChange={(e) =>
                                    formik.setFieldValue(
                                      `dematAccounts.${index}.dematAccountId`,
                                      e.target.value
                                    )
                                  }
                                  onBlur={() =>
                                    formik.setFieldTouched(
                                      `dematAccounts.${index}.dematAccountId`
                                    )
                                  }
                                />
                              </FieldBlock>
                              <FieldBlock label="Depository">
                                <Select
                                  options={["NSDL", "CDSL"]}
                                  isPlaceHolderDisabled
                                  value={account.type}
                                  onChange={(e) =>
                                    formik.setFieldValue(
                                      `dematAccounts.${index}.type`,
                                      e.target.value
                                    )
                                  }
                                />
                              </FieldBlock>
                              <FieldBlock label="Status">
                                <Select
                                  options={[
                                    { value: "active", label: "Active" },
                                    {
                                      value: "inactive",
                                      label: "Inactive",
                                    },
                                  ]}
                                  valueGetter={(option) => option.value}
                                  textGetter={(option) => option.label}
                                  isPlaceHolderDisabled
                                  value={account.status}
                                  onChange={(e) =>
                                    formik.setFieldValue(
                                      `dematAccounts.${index}.status`,
                                      e.target.value
                                    )
                                  }
                                />
                              </FieldBlock>
                            </div>
                            <div className="mt-4 flex flex-col gap-2 border-t border-borderColor pt-4 sm:flex-row sm:items-center sm:justify-between">
                              <div>
                                <p className="text-sm font-medium text-gray-dark">
                                  CML document
                                </p>
                                <p className="text-xs text-gray-light">
                                  PDF only
                                </p>
                              </div>
                              <label className="cursor-pointer rounded-sm border border-inputBorder bg-white px-3 py-2 text-sm font-semibold text-gray-dark shadow-box hover:bg-tertiaryBg">
                                {account.fileName || "Attach PDF"}
                                <input
                                  className="hidden"
                                  type="file"
                                  accept="application/pdf"
                                  onChange={(event) => {
                                    const file = event.target.files?.[0];
                                    if (!file) return;
                                    if (
                                      file.type !== "application/pdf" ||
                                      file.size > 5 * 1024 * 1024
                                    ) {
                                      toast("Attach a PDF smaller than 5 MB", {
                                        type: "error",
                                      });
                                      event.target.value = "";
                                      return;
                                    }
                                    const reader = new FileReader();
                                    reader.onload = () => {
                                      const base64 = String(
                                        reader.result
                                      ).split(",")[1];
                                      formik.setFieldValue(
                                        `dematAccounts.${index}.file`,
                                        base64
                                      );
                                      formik.setFieldValue(
                                        `dematAccounts.${index}.fileName`,
                                        file.name
                                      );
                                    };
                                    reader.readAsDataURL(file);
                                  }}
                                />
                              </label>
                            </div>
                          </div>
                        )
                      )}
                      {!formik.values.dematAccounts?.length && (
                        <div className="rounded-lg border border-dashed border-inputBorder bg-white px-6 py-9 text-center">
                          <Icon
                            icon="material-symbols:account-balance-wallet-outline-rounded"
                            className="mx-auto mb-2 text-gray-light"
                            width={32}
                          />
                          <p className="text-sm font-medium text-gray-dark">
                            No Demat accounts added
                          </p>
                          <p className="mt-1 text-xs text-gray-light">
                            Add one only when it is available.
                          </p>
                        </div>
                      )}
                    </div>
                  </FormSection>

                  {employeeCustomFields.length > 0 && (
                    <FormSection
                      id="custom"
                      icon="material-symbols:tune-rounded"
                      title="Custom fields"
                      description="Additional information configured for your company."
                    >
                      <CustomFieldsSection
                        entityType={UdfEntityType.EMPLOYEE}
                        values={customFields}
                        onChange={setCustomFields}
                        embedded
                      />
                    </FormSection>
                  )}

                  {isEdit && (
                    <FormSection
                      id="effective-date"
                      icon="material-symbols:event-rounded"
                      title="Effective date"
                      description="Use this date for the employee history event."
                    >
                      <div className="max-w-sm">
                        <FieldBlock
                          label="Effective date"
                          hint="Today is selected by default. You can record a past effective date."
                        >
                          <Input
                            type="date"
                            value={effectiveDate}
                            max={currentLocalDate()}
                            onChange={(event) =>
                              setEffectiveDate(event.target.value)
                            }
                          />
                        </FieldBlock>
                      </div>
                    </FormSection>
                  )}

                  {!isEdit && boomerang.candidates.length > 0 && (
                    <BoomerangResolutionSection
                      headingRef={boomerangHeadingRef}
                      candidates={boomerang.candidates}
                      selectedCandidateId={boomerang.selectedCandidateId}
                      strategy={boomerang.strategy}
                      reason={boomerang.reason}
                      moveGrants={boomerang.moveGrants}
                      continueVesting={boomerang.continueVesting}
                      error={boomerang.error}
                      onCandidateChange={(candidateId) => {
                        dispatchBoomerang({
                          type: "candidateSelected",
                          candidateId,
                        });
                        if (
                          boomerang.strategy === "CREATE_SEPARATE_EMPLOYEE" &&
                          boomerang.candidates.find(
                            (candidate) => candidate.employeeId === candidateId
                          )?.alreadyLinked
                        ) {
                          dispatchBoomerang({
                            type: "strategyChanged",
                            strategy: "NOT_BOOMERANG",
                          });
                        }
                      }}
                      onStrategyChange={(strategy) => {
                        dispatchBoomerang({
                          type: "strategyChanged",
                          strategy,
                        });
                      }}
                      onReasonChange={(reason) =>
                        dispatchBoomerang({ type: "reasonChanged", reason })
                      }
                      onMoveGrantsChange={(moveGrants) =>
                        dispatchBoomerang({
                          type: "moveGrantsChanged",
                          moveGrants,
                        })
                      }
                      onContinueVestingChange={(continueVesting) =>
                        dispatchBoomerang({
                          type: "continueVestingChanged",
                          continueVesting,
                        })
                      }
                      onReviewLatest={() => {
                        dispatchBoomerang({
                          type: "candidatesFound",
                          candidates: [],
                        });
                        window.setTimeout(() => formik.submitForm(), 0);
                      }}
                    />
                  )}

                  {!isEdit &&
                    boomerang.error &&
                    boomerang.candidates.length === 0 && (
                      <HStack
                        className="items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4"
                        role="alert"
                      >
                        <Icon
                          icon="material-symbols:error-outline-rounded"
                          className="mt-0.5 shrink-0 text-red-600"
                          width={18}
                        />
                        <VStack className="gap-1">
                          <p className="text-sm font-medium text-red-700">
                            {"We couldn't save the employee"}
                          </p>
                          <p className="text-sm text-red-700">
                            {boomerang.error}. Correct the highlighted field and
                            try again.
                          </p>
                        </VStack>
                      </HStack>
                    )}

                  <div className="flex items-center justify-between pb-8 pt-2">
                    <ButtonSecondary
                      type="button"
                      onClick={() => navigate(returnTo)}
                    >
                      Cancel
                    </ButtonSecondary>
                    <ButtonPrimary
                      type="submit"
                      loading={creating || updating}
                      loadingText={isEdit ? "Saving..." : "Creating..."}
                      disabled={
                        (isEdit && !hasEffectiveChanges) ||
                        (!isEdit &&
                          boomerang.candidates.length > 0 &&
                          !boomerang.selectedCandidateId)
                      }
                    >
                      {isEdit
                        ? "Save changes"
                        : boomerang.candidates.length > 0 &&
                          boomerang.strategy === "MERGE_EXISTING_EMPLOYEE"
                        ? "Confirm and update employee"
                        : boomerang.candidates.length > 0 &&
                          boomerang.strategy !== "NOT_BOOMERANG"
                        ? "Confirm and create rejoin profile"
                        : "Create employee"}
                    </ButtonPrimary>
                  </div>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </VStack>
  );
}
